import React from 'react';
import { RatingStats } from '../../types/reviews';
import StarRating from './StarRating';

interface RatingSummaryProps {
  stats: RatingStats;
  className?: string;
  showDistribution?: boolean;
  showPercentages?: boolean;
}

const RatingSummary: React.FC<RatingSummaryProps> = ({
  stats,
  className = '',
  showDistribution = true,
  showPercentages = true
}) => {
  const { average_rating, rating_count, rating_distribution } = stats;

  // Calculate percentages for each rating
  const ratingPercentages = Object.entries(rating_distribution).map(([rating, count]) => ({
    rating: parseInt(rating),
    count,
    percentage: rating_count > 0 ? (count / rating_count) * 100 : 0
  })).reverse(); // Show 5 stars first

  // Helper to format the percentage
  const formatPercentage = (percentage: number) => {
    return percentage < 1 && percentage > 0 ? '<1' : Math.round(percentage);
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
      {/* Overall Rating */}
      <div className="flex items-center gap-4 mb-6">
        <div className="text-center">
          <div className="text-4xl font-bold text-gray-900 mb-1">
            {average_rating.toFixed(1)}
          </div>
          <StarRating 
            rating={average_rating} 
            size="lg" 
            showValue={false}
            precision={0.1}
          />
          <div className="text-sm text-gray-600 mt-1">
            {rating_count.toLocaleString()} {rating_count === 1 ? 'review' : 'reviews'}
          </div>
        </div>

        {/* Rating Distribution */}
        {showDistribution && (
          <div className="flex-1 max-w-md">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Rating Distribution</h4>
            <div className="space-y-2">
              {ratingPercentages.map(({ rating, count, percentage }) => (
                <div key={rating} className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 w-6">{rating}</span>
                  <StarRating 
                    rating={1} 
                    maxRating={1} 
                    size="sm" 
                    className="flex-shrink-0"
                  />
                  
                  {/* Progress bar */}
                  <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-0">
                    <div 
                      className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  
                  <div className="flex items-center gap-1 text-sm text-gray-600 w-16 justify-end">
                    {showPercentages && (
                      <span>{formatPercentage(percentage)}%</span>
                    )}
                    <span className="text-gray-400">({count})</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
        <div className="text-center">
          <div className="text-2xl font-semibold text-green-600">
            {ratingPercentages
              .filter(item => item.rating >= 4)
              .reduce((sum, item) => sum + item.percentage, 0)
              .toFixed(0)}%
          </div>
          <div className="text-xs text-gray-600">Positive Reviews</div>
        </div>
        
        <div className="text-center">
          <div className="text-2xl font-semibold text-blue-600">
            {average_rating.toFixed(1)}
          </div>
          <div className="text-xs text-gray-600">Average Rating</div>
        </div>
        
        <div className="text-center">
          <div className="text-2xl font-semibold text-gray-700">
            {rating_count.toLocaleString()}
          </div>
          <div className="text-xs text-gray-600">Total Reviews</div>
        </div>
      </div>
    </div>
  );
};

export default RatingSummary; 